<template>
  <!-- H3 hex grid layer — renders time-to-next-overpass colored hexagons -->
  <Teleport to="body">
    <div
      ref="tooltipEl"
      class="hex-grid-tooltip"
      v-show="hoveredCellData"
      :style="tooltipStyle"
    >
      <div v-if="hoveredCellData" class="hex-grid-tooltip-body">
        <strong>{{ $t('hexTooltip.nextOverpass') }}</strong>
        <template v-if="hoveredCellData.next_time">
          <div class="hex-grid-tooltip-row">
            <span class="hex-grid-tooltip-label">{{ $t('hexTooltip.satellite') }}</span>
            <span>{{ hoveredCellData.next_sat }}</span>
          </div>
          <div class="hex-grid-tooltip-row">
            <span class="hex-grid-tooltip-label">{{ $t('hexTooltip.acquisition') }}</span>
            <span>{{ fmtTime(hoveredCellData.next_time) }}</span>
          </div>
          <div v-if="hoveredCellData.direction" class="hex-grid-tooltip-row">
            <span class="hex-grid-tooltip-label">{{ $t('popup.direction') }}</span>
            <span>{{ hoveredCellData.direction === 'ASCENDING' ? $t('popup.ascending') : $t('popup.descending') }}</span>
          </div>
          <div class="hex-grid-tooltip-row">
            <span class="hex-grid-tooltip-label">{{ $t('hexTooltip.cloudForecast') }}</span>
            <span>{{ hoveredCellData.next_cloud_cover_pct != null ? `${hoveredCellData.next_cloud_cover_pct}%` : $t('popup.notCalculated') }}</span>
          </div>
          <div v-if="hoveredCellData.expected_latency_minutes != null" class="hex-grid-tooltip-row">
            <span class="hex-grid-tooltip-label">{{ $t('popup.expectedLatency') }}</span>
            <span>~{{ Math.round(hoveredCellData.expected_latency_minutes) }} min</span>
          </div>
          <div v-if="hoveredCellData.expected_delivery" class="hex-grid-tooltip-row">
            <span class="hex-grid-tooltip-label">{{ $t('popup.expectedDelivery') }}</span>
            <span>{{ fmtTime(hoveredCellData.expected_delivery) }}</span>
          </div>
        </template>
        <div v-else class="hex-grid-tooltip-row">
          <span class="hex-grid-tooltip-label">{{ $t('hexTooltip.status') }}</span>
          <span>{{ $t('hexTooltip.noOverpass') }}</span>
        </div>
        <div class="hex-grid-tooltip-row">
          <span class="hex-grid-tooltip-label">{{ $t('hexTooltip.timeIn') }}</span>
          <span>{{ fmtHours(hoveredCellData.min_hours) }}</span>
        </div>
        <div class="hex-grid-tooltip-row">
          <span class="hex-grid-tooltip-label">{{ $t('hexTooltip.totalPasses') }}</span>
          <span>{{ hoveredCellData.num_passes }}</span>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, computed } from 'vue';
import VectorTileLayer from 'ol/layer/VectorTile';
import { PMTilesVectorSource } from 'ol-pmtiles';
import { Fill, Stroke, Style } from 'ol/style';
import type { MapBrowserEvent } from 'ol';
import { FetchSource, type Source as PMTilesSource } from 'pmtiles';

import { toLonLat } from 'ol/proj';
import { useAuth } from '@/composables/useAuth';
import { useMap } from '@/composables/useMap';
import { useFilterStore } from '@/composables/useFilterStore';
import { useDateTimeSettings } from '@/composables/useDateTimeSettings';
import { getTimingColorForHours, isTimingUnavailable, NO_UPCOMING_HOURS } from '@/composables/useTimingColorScale';
import { isColorblindFriendly } from '@/composables/useAccessibilitySettings';
import { isOroraTechConstellationSatellite } from '@/composables/useConstellationGrouping';
import { normalizePassDirection, type PassDirection } from '@/composables/passDirection';
import {
  useSatelliteLatency,
  computeExpectedDeliveryForPass,
  getExpectedLatencyMinutesForPass,
} from '@/composables/useSatelliteLatency';

const { map } = useMap();
useSatelliteLatency();
const { accessToken, authDisabled } = useAuth();
const {
  loading,
  mapViewMode,
  selectedHexCells,
  hexTimingSelectedPasses,
  hexCellCentroids,
  visiblePasses,
  visiblePassCount,
  selectedPassId,
  selectedSatelliteNames,
  otDataSourceFilter,
  timeRange,
  hexGridVisible,
  hexGridOpacity,
  cloudCoverThreshold,
  standardForecastEndMs,
  passWithinForecastHorizon,
  // Timing shards (viewport coloring + pass list)
  hexTimingData,
  hexTimingDataRevision,
  hexTimingIndex,
  hexTimingLastMergeAt,
  hasTimingDataForCell,
  hasPassDataForCell,
  ensureTimingShardsForCells,
  prefetchTimingShards,
  hasTimingShardLoadsInFlight,
  loadHexTimingIndex,
  hexPassData,
  ensurePassShardsForCells,
  loadHexPassIndex,
  hexTimingPerf,
} = useFilterStore();

const tooltipStyle = ref<Record<string, string>>({});
const { formatDateTime } = useDateTimeSettings();

interface HexCellData {
  h3_index: string;
  min_hours: number;
  next_time: string;
  next_sat: string;
  direction?: PassDirection;
  next_cloud_cover_pct: number | null;
  expected_latency_minutes: number | null;
  expected_delivery: string | null;
  num_passes: number;
}

const hoveredCellData = ref<HexCellData | null>(null);
const DEFAULT_PASS_DURATION_MS = 5 * 60 * 1000;

function parseUtcEpochMs(value: string): number {
  const raw = String(value ?? '').trim();
  if (!raw) return Number.NaN;
  if (/[zZ]$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(raw)) {
    return Date.parse(raw);
  }
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  return Date.parse(`${normalized}Z`);
}

function getColorForHours(hours: number): string {
  return isColorblindFriendly.value
    ? getTimingColorForHours(hours, 1, 0.9)
    : getTimingColorForHours(hours, 0.55, 0.6);
}

const selectedStroke = new Stroke({ color: 'rgba(3, 222, 204, 0.9)', width: 3 });
const defaultStroke = new Stroke({ color: 'rgba(255, 255, 255, 0.65)', width: 1.0 });

/* ── Viewport-based timing shard loading (lightweight, for accurate colors) ── */
const _pendingTimingCells = new Set<string>();
let _timingLoadTimer: ReturnType<typeof setTimeout> | null = null;
const _pendingPrefetchShardKeys = new Set<string>();
let _prefetchTimer: ReturnType<typeof setTimeout> | null = null;
const MAX_VIEWPORT_TIMING_SHARDS = 12;
const MAX_VIEWPORT_PREFETCH_SHARDS = 4;
const INITIAL_FLUSH_DELAY_MS = 80;
const MOVEEND_FLUSH_DELAY_MS = 120;
const MOVEEND_FLUSH_DELAY_WHILE_LOADING_MS = 40;

function _scorePrefetchDistance(
  shardKey: string,
  activeKeys: string[],
): number {
  const shardValue = Number.parseInt(shardKey, 16);
  if (!Number.isFinite(shardValue) || activeKeys.length === 0) return Number.POSITIVE_INFINITY;
  let best = Number.POSITIVE_INFINITY;
  for (const key of activeKeys) {
    const activeValue = Number.parseInt(key, 16);
    if (!Number.isFinite(activeValue)) continue;
    const delta = Math.abs(activeValue - shardValue);
    if (delta < best) best = delta;
  }
  return best;
}

function _drainPrefetchShardQueue() {
  _prefetchTimer = null;
  if (mapViewMode.value !== 'grid') return;
  if (_pendingPrefetchShardKeys.size === 0) return;
  if (hasTimingShardLoadsInFlight()) {
    _prefetchTimer = setTimeout(_drainPrefetchShardQueue, MOVEEND_FLUSH_DELAY_MS);
    return;
  }
  const keys = [..._pendingPrefetchShardKeys];
  _pendingPrefetchShardKeys.clear();
  void prefetchTimingShards(keys);
}

function _scheduleIdlePrefetch(keys: string[]) {
  for (const key of keys) {
    _pendingPrefetchShardKeys.add(key);
    if (_pendingPrefetchShardKeys.size >= MAX_VIEWPORT_PREFETCH_SHARDS) break;
  }
  if (_pendingPrefetchShardKeys.size === 0 || _prefetchTimer) return;
  _prefetchTimer = setTimeout(_drainPrefetchShardQueue, 220);
}

async function flushPendingTimingLoad() {
  _timingLoadTimer = null;
  const cells = [..._pendingTimingCells];
  _pendingTimingCells.clear();
  if (cells.length === 0) return;

  // Cap unique shard keys
  const prefixLen = hexTimingIndex.value?.prefixLength ?? 4;
  const activeKeys = new Set<string>();
  const overflowKeys = new Set<string>();
  const cappedCells: string[] = [];
  for (const cellId of cells) {
    if (hasTimingDataForCell(cellId)) continue;
    const key = cellId.slice(0, prefixLen);
    if (activeKeys.size >= MAX_VIEWPORT_TIMING_SHARDS && !activeKeys.has(key)) {
      overflowKeys.add(key);
      continue;
    }
    activeKeys.add(key);
    cappedCells.push(cellId);
  }

  if (cappedCells.length > 0) {
    await ensureTimingShardsForCells(cappedCells);
  }
  if (overflowKeys.size > 0) {
    const baseKeys = [...activeKeys];
    const prefetchCandidates = [...overflowKeys]
      .sort((a, b) => _scorePrefetchDistance(a, baseKeys) - _scorePrefetchDistance(b, baseKeys))
      .slice(0, MAX_VIEWPORT_PREFETCH_SHARDS);
    _scheduleIdlePrefetch(prefetchCandidates);
  }
}

function scheduleViewportTimingLoad(mode: 'initial' | 'moveend' = 'moveend') {
  if (_timingLoadTimer) return;
  const delay = mode === 'initial'
    ? INITIAL_FLUSH_DELAY_MS
    : (hasTimingShardLoadsInFlight()
      ? MOVEEND_FLUSH_DELAY_WHILE_LOADING_MS
      : MOVEEND_FLUSH_DELAY_MS);
  _timingLoadTimer = setTimeout(flushPendingTimingLoad, delay);
}

function onMapMoveEndTimingShards() {
  if (_pendingTimingCells.size > 0) {
    scheduleViewportTimingLoad('moveend');
  }
}

/* ── Style cache to avoid creating many identical Style objects ── */
const styleCache = new Map<string, Style>();

function getHexStyle(hours: number, isSelected: boolean): Style | undefined {
  const fillColor = getColorForHours(hours);
  const key = `${fillColor}-${isSelected}`;
  let style = styleCache.get(key);
  if (!style) {
    style = new Style({
      fill: new Fill({ color: fillColor }),
      stroke: isSelected ? selectedStroke : defaultStroke,
    });
    styleCache.set(key, style);
  }
  return style;
}

interface DynamicHexInfo {
  min_hours: number;
  next_time: string;
  next_sat: string;
  next_cloud_cover_pct: number | null;
  num_passes: number;
}

interface DetailedNextPassInfo {
  satellite: string;
  startMs: number;
  endTimeIso: string;
  direction?: PassDirection;
  cloudCoverPct: number | null;
}

function normalizeCloudCoverPct(value: unknown): number | null {
  if (value == null) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.min(100, Math.round(numeric)));
}

/** Binary search: find first index in sorted arr where arr[i] >= target */
function _lowerBound(arr: number[], target: number): number {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (arr[mid] < target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Binary search for v3 interleaved arrays: [t0, c0, t1, c1, ...]
 * Returns the index of the first timestamp >= target (always even).
 */
function _lowerBoundV3(arr: number[], target: number): number {
  let lo = 0;
  let hi = arr.length >>> 1; // number of (time, cloud) pairs
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (arr[mid * 2] < target) lo = mid + 1;
    else hi = mid;
  }
  return lo * 2;
}

function getFallbackHexInfo(props: Record<string, unknown>): DynamicHexInfo | null {
  const refTime = timeRange.value[0];

  // Fallback uses only the lightweight properties kept in PMTiles
  // (sats, cell_start, cell_end were removed to reduce tile size).
  // Timing shards provide accurate per-satellite filtering once loaded.
  const nextTime = String(props.next_time ?? '');
  const nextTimeMs = parseUtcEpochMs(nextTime);
  const nextSatellite = String(props.next_sat ?? '');
  if (
    !passWithinForecastHorizon(nextSatellite, nextTimeMs)
    || nextTimeMs > timeRange.value[1]
  ) {
    return {
      min_hours: NO_UPCOMING_HOURS,
      next_time: '',
      next_sat: '',
      next_cloud_cover_pct: null,
      num_passes: 0,
    };
  }
  const nextHours = Number.isFinite(nextTimeMs)
    ? Math.max(0, (nextTimeMs - refTime) / 3_600_000)
    : Number(props.min_hours ?? 48);

  return {
    min_hours: Number.isFinite(nextHours) ? nextHours : 48,
    next_time: nextTime,
    next_sat: nextSatellite,
    next_cloud_cover_pct: nextTimeMs <= standardForecastEndMs.value
      ? normalizeCloudCoverPct(props.next_cloud_cover_pct)
      : null,
    num_passes: Number(props.num_passes ?? 0),
  };
}

/* ── Timing-shard-backed hex info (accurate per-satellite, lightweight) ── */
const timingInfoCache = new Map<string, { signature: string; info: DynamicHexInfo | null }>();

function getDetailedNextCloudCover(cellId: string): number | null {
  const detailedCell = hexPassData.value?.[cellId];
  if (!Array.isArray(detailedCell) || detailedCell.length === 0) return null;

  const refTime = timeRange.value[0];
  const satSet = new Set(selectedSatelliteNames.value.map((s) => s.toUpperCase()));
  const expectedSource = otDataSourceFilter.value === 'predicted' ? 'api' : 'forest_client';
  const maxCloud = cloudCoverThreshold.value;

  let nextStartMs = Number.POSITIVE_INFINITY;
  let nextCloudCover: number | null = null;

  for (const entry of detailedCell) {
    const satName = String(entry?.satellite ?? '').trim();
    if (!satName || !satSet.has(satName.toUpperCase())) continue;

    const dataSource = typeof entry?.data_source === 'string' ? entry.data_source : undefined;
    if (isOroraTechConstellationSatellite(satName) && dataSource && dataSource !== expectedSource) {
      continue;
    }

    const startMs = parseUtcEpochMs(String(entry?.start_time ?? ''));
    const hasCloudForecast = startMs <= standardForecastEndMs.value;

    // Skip passes exceeding cloud threshold only inside the cloud forecast horizon.
    if (maxCloud < 100 && hasCloudForecast) {
      const cc = normalizeCloudCoverPct(entry?.cloud_cover_pct);
      if (cc != null && cc > maxCloud) continue;
    }

    if (!passWithinForecastHorizon(satName, startMs, dataSource)) continue;
    if (!Number.isFinite(startMs) || startMs < refTime || startMs >= nextStartMs) continue;

    nextStartMs = startMs;
    nextCloudCover = hasCloudForecast ? normalizeCloudCoverPct(entry?.cloud_cover_pct) : null;
  }

  return nextStartMs === Number.POSITIVE_INFINITY ? null : nextCloudCover;
}

function getTimingHexInfo(cellId: string): DynamicHexInfo | null {
  const timingCell = hexTimingData.value?.[cellId];
  if (!timingCell) return null;

  // Compute filter signature directly from live refs on every call.
  // This avoids any race between watcher-updated cached state and the actual
  // ref values, which was the root cause of stale colors/passes in production.
  const refTime = timeRange.value[0];
  const endTime = timeRange.value[1];
  const satSet = new Set(selectedSatelliteNames.value.map((s) => s.toUpperCase()));
  const expectedSource = otDataSourceFilter.value === 'predicted' ? 'api' : 'forest_client';
  const maxCloud = cloudCoverThreshold.value;
  const signature = `${refTime}:${endTime}:${standardForecastEndMs.value}:${[...satSet].sort().join('|')}:${expectedSource}:${maxCloud}`;

  const cached = timingInfoCache.get(cellId);
  if (cached && cached.signature === signature) return cached.info;

  // Convert epoch-ms time range to epoch-seconds for comparison
  const refTimeSec = Math.floor(refTime / 1000);
  const endTimeSec = Math.floor(endTime / 1000);

  let inRangeCount = 0;
  let nextStartSec = Infinity;
  let nextSatName = '';

  for (const satKey in timingCell) {
    const [satName, dataSource] = satKey.split('|');

    if (!satSet.has(satName.toUpperCase())) continue;

    if (isOroraTechConstellationSatellite(satName)) {
      if (dataSource && dataSource !== expectedSource) continue;
    }

    const satelliteEndTimeSec = isOroraTechConstellationSatellite(satName) && dataSource !== 'forest_client'
      ? endTimeSec
      : Math.min(endTimeSec, Math.floor(standardForecastEndMs.value / 1000));
    if (satelliteEndTimeSec < refTimeSec) continue;

    const startTimes = timingCell[satKey];
    const isV3 = hexTimingIndex.value?.version === 3;

    if (isV3) {
      // v3: interleaved [t0, c0, t1, c1, ...] — step by 2
      const startIdx = _lowerBoundV3(startTimes, refTimeSec);
      for (let i = startIdx; i < startTimes.length; i += 2) {
        if (startTimes[i] > satelliteEndTimeSec) break;
        const cloudPct = startTimes[i] * 1000 <= standardForecastEndMs.value && i + 1 < startTimes.length
          ? startTimes[i + 1]
          : -1;
        if (maxCloud < 100 && cloudPct !== -1 && cloudPct > maxCloud) continue;
        inRangeCount++;
        if (startTimes[i] < nextStartSec) {
          nextStartSec = startTimes[i];
          nextSatName = satName;
        }
      }
    } else {
      // v2: plain [t0, t1, t2, ...] — step by 1
      const startIdx = _lowerBound(startTimes, refTimeSec);
      if (
        startIdx < startTimes.length
        && startTimes[startIdx] <= satelliteEndTimeSec
        && startTimes[startIdx] < nextStartSec
      ) {
        nextStartSec = startTimes[startIdx];
        nextSatName = satName;
      }
      for (let i = startIdx; i < startTimes.length; i++) {
        if (startTimes[i] > satelliteEndTimeSec) break;
        inRangeCount++;
      }
    }
  }

  let info: DynamicHexInfo;
  if (nextStartSec === Infinity) {
    info = {
      min_hours: NO_UPCOMING_HOURS,
      next_time: '',
      next_sat: '',
      next_cloud_cover_pct: null,
      num_passes: inRangeCount,
    };
  } else {
    const nextStartMs = nextStartSec * 1000;
    info = {
      min_hours: (nextStartMs - refTime) / 3_600_000,
      next_time: new Date(nextStartMs).toISOString(),
      next_sat: nextSatName,
      next_cloud_cover_pct: getDetailedNextCloudCover(cellId),
      num_passes: inRangeCount,
    };
  }

  timingInfoCache.set(cellId, { signature, info });
  return info;
}

function getDetailedNextPass(cellId: string): DetailedNextPassInfo | null {
  const detailedCell = hexPassData.value?.[cellId];
  if (!Array.isArray(detailedCell) || detailedCell.length === 0) return null;

  const refTime = timeRange.value[0];
  const satSet = new Set(selectedSatelliteNames.value.map((s) => s.toUpperCase()));
  const expectedSource = otDataSourceFilter.value === 'predicted' ? 'api' : 'forest_client';
  const maxCloud = cloudCoverThreshold.value;

  let best: DetailedNextPassInfo | null = null;

  for (const entry of detailedCell) {
    const satName = String(entry?.satellite ?? '').trim();
    if (!satName || !satSet.has(satName.toUpperCase())) continue;

    const dataSource = typeof entry?.data_source === 'string' ? entry.data_source : undefined;
    if (isOroraTechConstellationSatellite(satName) && dataSource && dataSource !== expectedSource) {
      continue;
    }

    const startMs = parseUtcEpochMs(String(entry?.start_time ?? ''));
    if (!passWithinForecastHorizon(satName, startMs, dataSource)) continue;
    if (!Number.isFinite(startMs) || startMs < refTime) continue;
    const cloudCoverPct = startMs <= standardForecastEndMs.value
      ? normalizeCloudCoverPct(entry?.cloud_cover_pct)
      : null;
    if (maxCloud < 100 && cloudCoverPct != null && cloudCoverPct > maxCloud) continue;

    const endIsoRaw = String(entry?.end_time ?? '').trim();
    const endMs = parseUtcEpochMs(endIsoRaw);
    const endTimeIso = Number.isFinite(endMs)
      ? new Date(endMs).toISOString()
      : new Date(startMs + DEFAULT_PASS_DURATION_MS).toISOString();

    if (!best || startMs < best.startMs) {
      best = {
        satellite: satName,
        startMs,
        endTimeIso,
        direction: normalizePassDirection(entry?.d),
        cloudCoverPct,
      };
    }
  }

  return best;
}

function buildPmtilesHeaders(): Headers {
  const headers = new Headers();
  const token = authDisabled ? null : accessToken.value;
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

/* ── Layer ── */
const hexPmtilesSource = new FetchSource('/data/satellite_hex_grid.pmtiles', buildPmtilesHeaders());
const hexPmtilesUrl = hexPmtilesSource as unknown as string & PMTilesSource;
const hexSource = new PMTilesVectorSource({
  url: hexPmtilesUrl,
  maxZoom: 7,
});

watch(accessToken, () => {
  hexPmtilesSource.setHeaders(buildPmtilesHeaders());
  hexSource.refresh();
});

const hexLayer = new VectorTileLayer({
  source: hexSource,
  declutter: false,
  renderBuffer: 50,
  updateWhileAnimating: false,
  updateWhileInteracting: false,
  style: (feature) => {
    if (loading.value) return undefined;
    const props = feature.getProperties();
    const cellId = String(props.h3_index ?? '');

    // Queue cells that need timing data (loaded by moveend / flush, not here)
    if (cellId && !hasTimingDataForCell(cellId)) {
      _pendingTimingCells.add(cellId);
      if (!_timingLoadTimer && mapViewMode.value === 'grid') {
        scheduleViewportTimingLoad('moveend');
      }
    }

    const isSelected = selectedHexCells.value.has(cellId);
    const info = getTimingHexInfo(cellId)
      ?? getFallbackHexInfo(props as Record<string, unknown>);
    if (!info) return undefined;
    return getHexStyle(info.min_hours, isSelected);
  },
});

/** Trigger an immediate OL layer re-render + optional pass list update.
 *
 * Pre-MR, all render triggers called hexLayer.changed() directly from Vue
 * watchers (microtasks). OL picks up the revision change on its next internal
 * animation frame. The previous RAF-coalesced approach (scheduleHexLayerRefresh)
 * introduced a double-indirection (our RAF → hexLayer.changed() → OL's RAF)
 * that could miss OL's current frame, leaving stale styles on screen in
 * production where components mount once and there is no HMR to reset state.
 *
 * Direct calls let OL's own render loop handle coalescing (multiple
 * hexLayer.changed() calls before the next frame result in one render).
 */
function refreshHexLayer(options?: {
  updatePasses?: boolean;
  paintMergeAt?: number;
}) {
  hexLayer.changed();
  if (options?.updatePasses && selectedHexCells.value.size > 0) {
    updateVisiblePassesFromHex();
  }
  if ((options?.paintMergeAt ?? 0) > 0) {
    hexTimingPerf.recordSample(
      'first_accurate_paint',
      performance.now() - (options?.paintMergeAt ?? 0),
    );
  }
}

/* ── Helpers ── */
function fmtTime(iso: string): string {
  return formatDateTime(iso);
}

function fmtHours(h: number): string {
  if (isTimingUnavailable(h)) return 'No upcoming pass';
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${h.toFixed(1)} hours`;
  return `${(h / 24).toFixed(1)} days`;
}

function getCellCenterLat(cellId: string, feature?: unknown): number | null {
  const cached = hexCellCentroids.get(cellId);
  if (cached) return cached[0];

  const geometry = (feature as { getGeometry?: () => { getExtent: () => [number, number, number, number] } | null } | null)?.getGeometry?.();
  const extent = geometry?.getExtent?.();
  if (!extent || !Number.isFinite(extent[0])) return null;

  const cx = (extent[0] + extent[2]) / 2;
  const cy = (extent[1] + extent[3]) / 2;
  const [lon, lat] = toLonLat([cx, cy]);
  hexCellCentroids.set(cellId, [lat, lon]);
  return lat;
}

/* ── Map interaction ── */
let viewportEl: HTMLElement | null = null;
let _lastHoveredCellId: string | null = null;

function clearHoverState() {
  _lastHoveredCellId = null;
  hoveredCellData.value = null;
  if (viewportEl) viewportEl.style.cursor = '';
}

function onPointerLeave() {
  clearHoverState();
}

function onDocumentPointerMove(evt: PointerEvent) {
  if (!viewportEl || mapViewMode.value !== 'grid') return;
  const target = evt.target;
  if (!(target instanceof Node)) {
    clearHoverState();
    return;
  }
  if (!viewportEl.contains(target)) {
    clearHoverState();
  }
}

/* ── Throttled hover to reduce Canvas2D getImageData calls ── */
let _hoverTimer: ReturnType<typeof setTimeout> | null = null;
let _lastHoverEvt: MapBrowserEvent<PointerEvent> | null = null;
const HOVER_THROTTLE_MS = 75;

function onHexHoverThrottled(evt: MapBrowserEvent<PointerEvent>) {
  _lastHoverEvt = evt;
  if (_hoverTimer) return;
  _hoverTimer = setTimeout(() => {
    _hoverTimer = null;
    if (_lastHoverEvt) onHexHover(_lastHoverEvt);
  }, HOVER_THROTTLE_MS);
}

function addLayer() {
  const m = map.value;
  if (!m) return;
  m.addLayer(hexLayer);
  (m as any).on('singleclick', onHexClick);
  (m as any).on('pointermove', onHexHoverThrottled);
  m.on('moveend', onMapMoveEndTimingShards);
  viewportEl = m.getViewport() as HTMLElement | null;
  if (viewportEl) viewportEl.addEventListener('pointerleave', onPointerLeave);
  window.addEventListener('pointermove', onDocumentPointerMove, { passive: true });
}

function removeLayer() {
  const m = map.value;
  if (!m) return;
  (m as any).un('singleclick', onHexClick);
  (m as any).un('pointermove', onHexHoverThrottled);
  m.un('moveend', onMapMoveEndTimingShards);
  window.removeEventListener('pointermove', onDocumentPointerMove);
  if (viewportEl) {
    viewportEl.removeEventListener('pointerleave', onPointerLeave);
    viewportEl = null;
  }
  m.removeLayer(hexLayer);
}

function onHexClick(evt: MapBrowserEvent<PointerEvent>) {
  const m = map.value;
  if (!m || mapViewMode.value !== 'grid') return;

  let foundH3Index: string | null = null;

  m.forEachFeatureAtPixel(
    evt.pixel,
    (feature) => {
      const props = feature.getProperties();
      const cellId = String(props.h3_index ?? '');
      const info = getTimingHexInfo(cellId) ?? getFallbackHexInfo(props as Record<string, unknown>);
      if (!info) return;
      foundH3Index = cellId;

      // Cache centroid for solar daytime calculation (only needed for selected cells)
      if (cellId && !hexCellCentroids.has(cellId)) {
        const geom = feature.getGeometry();
        if (geom) {
          const ext = geom.getExtent();
          const cx = (ext[0] + ext[2]) / 2;
          const cy = (ext[1] + ext[3]) / 2;
          const [lon, lat] = toLonLat([cx, cy]);
          hexCellCentroids.set(cellId, [lat, lon]);
        }
      }

      return true; // stop after first hit
    },
    { layerFilter: (l) => l === hexLayer },
  );

  if (!foundH3Index) {
    // Click on empty area — clear selection
    selectedHexCells.value = new Set();
  } else {
    const isCtrl = evt.originalEvent.ctrlKey || evt.originalEvent.metaKey;
    const newSet = new Set(isCtrl ? selectedHexCells.value : []);
    if (newSet.has(foundH3Index)) {
      newSet.delete(foundH3Index);
    } else {
      newSet.add(foundH3Index);
    }
    selectedHexCells.value = newSet;
  }

  // Re-style after selection change
  refreshHexLayer({ updatePasses: true });
}

function onHexHover(evt: MapBrowserEvent<PointerEvent>) {
  const m = map.value;
  if (!m || mapViewMode.value !== 'grid') {
    hoveredCellData.value = null;
    return;
  }

  let found: HexCellData | null = null;
  let foundCellId: string | null = null;

  m.forEachFeatureAtPixel(
    evt.pixel,
    (feature) => {
      const props = feature.getProperties();
      const cellId = String(props.h3_index ?? '');
      const info = getTimingHexInfo(cellId) ?? getFallbackHexInfo(props as Record<string, unknown>);
      if (!info) return;
      const fallbackCloudCover = normalizeCloudCoverPct(props.next_cloud_cover_pct);
      const centerLat = getCellCenterLat(cellId, feature);
      const detailedNextPass = getDetailedNextPass(cellId);
      const nextSat = detailedNextPass?.satellite || info.next_sat;
      const nextEndTimeIso = detailedNextPass?.endTimeIso || (() => {
        const nextStartMs = parseUtcEpochMs(info.next_time);
        if (!Number.isFinite(nextStartMs)) return '';
        return new Date(nextStartMs + DEFAULT_PASS_DURATION_MS).toISOString();
      })();
      const passForLatency = nextSat && nextEndTimeIso
        ? {
          name: nextSat,
          end_time: nextEndTimeIso,
          centroid_lat: centerLat,
        }
        : null;
      found = {
        h3_index: cellId,
        min_hours: info.min_hours,
        next_time: info.next_time,
        next_sat: nextSat,
        direction: detailedNextPass?.direction,
        next_cloud_cover_pct: detailedNextPass?.cloudCoverPct ?? info.next_cloud_cover_pct ?? fallbackCloudCover,
        expected_latency_minutes: passForLatency ? getExpectedLatencyMinutesForPass(passForLatency) : null,
        expected_delivery: passForLatency ? computeExpectedDeliveryForPass(passForLatency) : null,
        num_passes: info.num_passes,
      };
      foundCellId = cellId;
      if (cellId && !hasPassDataForCell(cellId)) {
        void ensurePassShardsForCells([cellId]);
      }
      return true;
    },
    { layerFilter: (l) => l === hexLayer },
  );

  const nextHoveredId = foundCellId;
  if (!nextHoveredId && !_lastHoveredCellId) {
    if (viewportEl) viewportEl.style.cursor = '';
    return;
  }
  if (nextHoveredId && nextHoveredId === _lastHoveredCellId) {
    hoveredCellData.value = found;
    if (viewportEl) viewportEl.style.cursor = 'pointer';
    return;
  }
  _lastHoveredCellId = nextHoveredId;
  hoveredCellData.value = found;

  if (found) {
    // Position tooltip near cursor
    const viewport = m.getViewport();
    if (viewport) {
      const vpRect = viewport.getBoundingClientRect();
      tooltipStyle.value = {
        position: 'fixed',
        left: `${vpRect.left + evt.pixel[0] + 12}px`,
        top: `${vpRect.top + evt.pixel[1] - 10}px`,
        zIndex: '10000',
      };
    }
    if (viewportEl) viewportEl.style.cursor = 'pointer';
  } else {
    if (viewportEl) viewportEl.style.cursor = '';
  }
}

/* ── Populate visiblePasses from selected hex cells ── */
function updateVisiblePassesFromHex() {
  if (mapViewMode.value !== 'grid') return;

  if (selectedHexCells.value.size === 0) {
    visiblePasses.value = [];
    visiblePassCount.value = 0;
    selectedPassId.value = null;
    return;
  }

  visiblePasses.value = hexTimingSelectedPasses.value;
  visiblePassCount.value = hexTimingSelectedPasses.value.length;
}

/* ── Visibility management ── */
const effectiveHexVisible = computed(() => hexGridVisible.value && mapViewMode.value === 'grid');
watch(effectiveHexVisible, (vis) => {
  hexLayer.setVisible(vis);
  if (vis) {
    loadHexTimingIndex();
    loadHexPassIndex();
    timingInfoCache.clear();
    refreshHexLayer({ updatePasses: true });
    // After first render populates _pendingTimingCells, load visible timing shards.
    scheduleViewportTimingLoad('initial');
    if (selectedHexCells.value.size > 0) {
      void ensurePassShardsForCells([...selectedHexCells.value]);
    }
  }
  if (!vis) {
    clearHoverState();
  }
}, { immediate: true });

// When metadata finishes loading, tiles that were rendered while loading=true
// have a cached undefined style (the style function returns early when loading).
// Without this watcher, those tiles never re-render because nothing calls
// hexLayer.changed(). Re-trigger a full refresh + viewport timing load so the
// style function can queue visible cells and render fallback colors immediately.
watch(loading, (isLoading) => {
  if (!isLoading && effectiveHexVisible.value) {
    timingInfoCache.clear();
    refreshHexLayer({ updatePasses: true });
    scheduleViewportTimingLoad('initial');
  }
});

watch(hexGridOpacity, (op) => {
  hexLayer.setOpacity(op);
}, { immediate: true });

watch(isColorblindFriendly, () => {
  styleCache.clear();
  if (mapViewMode.value === 'grid') refreshHexLayer();
});

// Invalidate cache when filter/time changes.
// Use explicit primitive getter sources instead of deep-watching the refs directly
// to guarantee Vue's dependency tracking fires reliably in production builds.
watch(
  [
    () => timeRange.value[0],
    () => timeRange.value[1],
    () => selectedSatelliteNames.value.join('|'),
    () => otDataSourceFilter.value,
    () => cloudCoverThreshold.value,
  ],
  () => {
    timingInfoCache.clear();
    if (mapViewMode.value === 'grid') {
      refreshHexLayer({ updatePasses: true });
      if (selectedHexCells.value.size > 0) {
        void ensurePassShardsForCells([...selectedHexCells.value]);
      }
    }
  },
);

// Reactively push selected-cell pass list whenever the computed pass list
// changes (covers time filter adjustments without needing an imperative call).
watch(hexTimingSelectedPasses, () => {
  if (mapViewMode.value === 'grid' && selectedHexCells.value.size > 0) {
    visiblePasses.value = hexTimingSelectedPasses.value;
    visiblePassCount.value = hexTimingSelectedPasses.value.length;
  }
});

watch(
  () => [...selectedHexCells.value].sort().join('|'),
  () => {
    if (mapViewMode.value !== 'grid' || selectedHexCells.value.size === 0) return;
    void ensurePassShardsForCells([...selectedHexCells.value]);
  },
  { immediate: true },
);

// Re-render when timing shards arrive (viewport coloring + pass list updates)
watch([hexTimingData, hexTimingDataRevision], () => {
  timingInfoCache.clear();
  if (mapViewMode.value === 'grid') {
    refreshHexLayer({
      updatePasses: true,
      paintMergeAt: hexTimingLastMergeAt.value,
    });
  }
});

watch(hexPassData, () => {
  timingInfoCache.clear();
  if (mapViewMode.value === 'grid' && hoveredCellData.value && _lastHoverEvt) {
    onHexHover(_lastHoverEvt);
  }
});

// Add/remove layer when map is ready
watch(map, (m) => { if (m) addLayer(); }, { immediate: true });
onBeforeUnmount(() => {
  removeLayer();
  if (_timingLoadTimer) { clearTimeout(_timingLoadTimer); _timingLoadTimer = null; }
  if (_prefetchTimer) { clearTimeout(_prefetchTimer); _prefetchTimer = null; }
  _pendingPrefetchShardKeys.clear();
  if (_hoverTimer) { clearTimeout(_hoverTimer); _hoverTimer = null; }
});
</script>

<style lang="scss">
.hex-grid-tooltip {
  pointer-events: none;
  background: var(--p-overlay-background, #1e293b);
  color: var(--p-text-color, #e2e8f0);
  border: 1px solid var(--p-overlay-border-color, rgba(255, 255, 255, 0.12));
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 0.78rem;
  line-height: 1.4;
  max-width: 280px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  white-space: nowrap;

  &-body {
    display: grid;
    grid-template-columns: max-content auto;
    column-gap: 6px;
    row-gap: 2px;

    > strong {
      grid-column: 1 / -1;
      font-size: 0.85rem;
      margin-bottom: 2px;
    }
  }

  &-row {
    display: contents;
  }

  &-label {
    color: var(--p-text-muted-color, #94a3b8);
  }
}

:root:not(.color-scheme-dark) {
  .hex-grid-tooltip {
    background: #ffffff;
    color: #0f172a;
    border-color: rgba(15, 23, 42, 0.16);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.16);

    .hex-grid-tooltip-label {
      color: #475569;
    }
  }
}
</style>
